import React from "react";
import type { Appointment, Company } from "../../../types";
import {
  getCarEffortColor,
  getPaymentMethodColor,
  getStatusBadgeColor,
  translateStatus,
} from "../../../utils/appointmentUtils";
import { formatTime, formatWeekdayShortDate } from "../../../utils/dateUtils";
import { ActionButton } from "../../shared";
import { CarBrandLogo } from "../../shared/CarBrandLogo";
import { Icons } from "../../shared/Icons";

function formatPrice(price: string): string {
  const value = Number.parseFloat(price);
  if (Number.isNaN(value)) return "R$ 0,00";
  return `R$ ${value.toFixed(2).replace(".", ",")}`;
}

function getStatusAccent(status?: string): string {
  switch ((status || "").toLowerCase()) {
    case "in_progress":
      return "border-l-blue-500 dark:!border-l-blue-400";
    case "completed":
      return "border-l-emerald-500 dark:!border-l-emerald-400";
    case "cancelled":
      return "border-l-rose-400 dark:!border-l-rose-400";
    default:
      return "border-l-slate-300 dark:!border-l-slate-500";
  }
}

interface ClientAppointmentCardProps {
  appointment: Appointment;
  company: Company | null;
  isPast?: boolean;
  onView: (appointmentId: number) => void;
  onEdit: (appointment: Appointment) => void;
  onPay: (appointment: Appointment) => void;
}

export const ClientAppointmentCard: React.FC<ClientAppointmentCardProps> = ({
  appointment,
  company,
  isPast = false,
  onView,
  onEdit,
  onPay,
}) => {
  const isCarCompany = company?.category === "car";
  const isCancelled = appointment.status?.toLowerCase() === "cancelled";
  const isDayService = appointment.service?.type === "day";
  const startTime = appointment.startTime
    ? formatTime(appointment.startTime)
    : null;
  const endTime = appointment.endTime ? formatTime(appointment.endTime) : null;

  return (
    <article
      className={`group rounded-2xl border-y border-r border-gray-100 dark:border-slate-700 border-l-4 shadow-sm hover:shadow-md hover:border-y-gray-200 hover:border-r-gray-200 dark:hover:border-y-slate-600 dark:hover:border-r-slate-600 transition-all duration-200 overflow-hidden ${getStatusAccent(
        appointment.status
      )} ${isPast ? "bg-slate-50/70 dark:bg-slate-900/70" : "bg-white dark:bg-slate-800"}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white capitalize leading-snug">
              {formatWeekdayShortDate(appointment.appointmentDate)}
            </p>
            {(isDayService || startTime) && (
              <p className="mt-1 text-sm text-gray-500 dark:text-slate-400 flex items-center gap-1.5">
                <Icons.Schedule sx={{ fontSize: 16 }} className="text-gray-400 dark:text-slate-500" />
                {isDayService
                  ? "Agendamento do dia"
                  : `${startTime}${endTime ? ` – ${endTime}` : ""}`}
              </p>
            )}
          </div>
          <span
            className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold border ${getStatusBadgeColor(
              appointment.status
            )}`}
          >
            {translateStatus(appointment.status)}
          </span>
        </div>

        <div className="mt-4 space-y-2.5">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-sky-50 dark:bg-sky-950/50 text-sky-600 dark:text-sky-400 flex items-center justify-center shrink-0">
              <Icons.Build sx={{ fontSize: 16 }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {appointment.service?.name || `Serviço #${appointment.serviceId}`}
              </p>
              {appointment.service?.description ? (
                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                  {appointment.service.description}
                </p>
              ) : null}
            </div>
          </div>

          {isCarCompany ? (
            <div
              className={`flex items-center gap-2.5 rounded-xl border px-3 py-2 ${
                appointment.clientCar
                  ? getCarEffortColor(appointment.clientCar.effort)
                  : "text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-900/50 border-gray-200 dark:border-slate-600"
              }`}
            >
              <CarBrandLogo
                brand={appointment.clientCar?.brand}
                className="shrink-0"
              />
              <div className="min-w-0 flex-1">
                {appointment.clientCar ? (
                  <>
                    <p className="text-sm font-medium truncate">
                      {appointment.clientCar.model}
                    </p>
                    <p className="text-xs opacity-80">
                      {appointment.clientCar.plate}
                    </p>
                  </>
                ) : (
                  <p className="text-sm">Sem veículo</p>
                )}
              </div>
              {appointment.isRecurrency ? (
                <span
                  className="shrink-0 text-sky-600 dark:text-sky-400"
                  title="Agendamento recorrente"
                >
                  <Icons.Repeat sx={{ fontSize: 18 }} />
                </span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-base sm:text-lg font-bold text-gray-900 dark:text-white tabular-nums">
            {formatPrice(appointment.price)}
          </p>
          {appointment.payment ? (
            <span
              className={`px-2.5 py-1 rounded-full text-xs font-medium border ${getPaymentMethodColor(
                appointment.payment
              )}`}
            >
              {appointment.payment}
            </span>
          ) : (
            <ActionButton
              variant="outline"
              size="sm"
              onClick={() => onPay(appointment)}
              className="text-xs px-2.5 py-1"
              icon={<Icons.Payment sx={{ fontSize: 14 }} />}
            >
              Pagar
            </ActionButton>
          )}
        </div>
      </div>

      <div className="px-4 sm:px-5 pb-4 sm:pb-5 flex items-center gap-2">
        <ActionButton
          variant="outline"
          size="sm"
          onClick={() => onView(appointment.id)}
          className="flex-1 justify-center text-xs sm:text-sm"
          icon={<Icons.Visibility sx={{ fontSize: 16 }} />}
        >
          Detalhes
        </ActionButton>
        <ActionButton
          variant="outline"
          size="sm"
          onClick={() => onEdit(appointment)}
          disabled={isCancelled}
          className="flex-1 justify-center text-xs sm:text-sm"
          icon={<Icons.Edit sx={{ fontSize: 16 }} />}
        >
          Editar
        </ActionButton>
      </div>
    </article>
  );
};
